/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import { __, sprintf } from '@web-stories-wp/i18n';

/**
 * Internal dependencies
 */
import {
  CoverrAttribution,
  TenorAttribution,
  UnsplashAttribution,
} from './attribution';
import { ContentType, ProviderType } from './constants';

/** @typedef {import('react').React.Component} ReactComponent */

/**
 *
 * @type {Object.<string, ProviderConfiguration>}
 */

type ProviderConfiguration = {
  /** A function that constructs an attribution React Component for this provider. */
  attributionComponent?: JSX.Element;
  /** The display name of the provider. */
  displayName?: string;
  defaultPreviewWidth?: number;
  /** An optional feature that must be turned on for the provider tab to be displayed. */
  featureName?: string;
  /** The content type to filter by. */
  contentTypeFilter?: ContentType;
  /** An error message to show if fetching media from this provider fails. */
  fetchMediaErrorMessage: string;
  /**
   * An error message to show if fetching categories from this provider fails.
   * Only needs to be specified if `supportsCategories` is true.
   */
  fetchCategoriesErrorMessage?: string;
  /** Whether this provider requires showing author attribution on each media element. */
  requiresAuthorAttribution: boolean;
  /** Whether this provider supports filtering media by category. */
  supportsCategories: boolean;
  /** Whether this provider supports filtering media by tags. */
  supportsSearchByTags: boolean;
  /** Whether this provider supports free-text media search. Defaults to true. */
  supportsSearch?: boolean;
  /** Whether media can only be listed after choosing a folder or collection. */
  requiresFolderSelection?: boolean;
  /** Whether this provider sends folder as a separate query parameter instead of in filter string. */
  usesFolderAsQueryParam?: boolean;
};

export const PROVIDERS: Record<ProviderType, ProviderConfiguration> = {
  [ProviderType.UNSPLASH]: {
    supportsCategories: false,
    supportsSearchByTags: false,
    requiresAuthorAttribution: true,
    attributionComponent: UnsplashAttribution,
    fetchMediaErrorMessage: sprintf(
      /* translators: %s: media provider name. */
      __('Error loading media from %s', 'web-stories'),
      'Unsplash'
    ),
    fetchCategoriesErrorMessage: sprintf(
      /* translators: %s: media provider name. */
      __('Error loading categories from %s', 'web-stories'),
      'Unsplash'
    ),
  },
  [ProviderType.COVERR]: {
    supportsCategories: false,
    supportsSearchByTags: false,
    requiresAuthorAttribution: false,
    attributionComponent: CoverrAttribution,
    fetchMediaErrorMessage: sprintf(
      /* translators: %s: media provider name. */
      __('Error loading media from %s', 'web-stories'),
      'Coverr'
    ),
    defaultPreviewWidth: 640,
  },
  [ProviderType.TENOR]: {
    contentTypeFilter: ContentType.GIF,
    supportsCategories: false,
    supportsSearchByTags: false,
    requiresAuthorAttribution: false,
    attributionComponent: TenorAttribution,
    fetchMediaErrorMessage: sprintf(
      /* translators: %s: media provider name. */
      __('Error loading media from %s', 'web-stories'),
      'Tenor'
    ),
  },
  [ProviderType.GETTY]: {
    supportsCategories: false,
    supportsSearchByTags: false,
    requiresAuthorAttribution: false,
    fetchMediaErrorMessage: sprintf(
      /* translators: %s: media provider name. */
      __('Error loading media from %s', 'web-stories'),
      'GettyImages'
    ),
  },
  [ProviderType.MIMIR]: {
    supportsCategories: false,
    supportsSearchByTags: false,
    requiresAuthorAttribution: false,
    fetchMediaErrorMessage: sprintf(
      /* translators: %s: media provider name. */
      __('Error loading media from %s', 'web-stories'),
      'Mimir'
    ),
    defaultPreviewWidth: 640,
  },
  [ProviderType.SLACK]: {
    supportsCategories: false,
    supportsSearchByTags: false,
    requiresAuthorAttribution: false,
    fetchMediaErrorMessage: sprintf(
      /* translators: %s: media provider name. */
      __('Error loading media from %s', 'web-stories'),
      'Slack'
    ),
    defaultPreviewWidth: 640,
  },
  [ProviderType.CAPTURE]: {
    supportsCategories: false,
    supportsSearchByTags: false,
    requiresAuthorAttribution: false,
    fetchMediaErrorMessage: sprintf(
      /* translators: %s: media provider name. */
      __('Error loading media from %s', 'web-stories'),
      'Capture'
    ),
  },
  [ProviderType.CLOUDINARY]: {
    supportsCategories: false,
    supportsSearchByTags: false,
    requiresAuthorAttribution: false,
    fetchMediaErrorMessage: sprintf(
      /* translators: %s: media provider name. */
      __('Error loading media from %s', 'web-stories'),
      'Cloudinary'
    ),
  },
  [ProviderType.SCOREPLAY]: {
    supportsCategories: false,
    supportsSearchByTags: true,
    requiresAuthorAttribution: false,
    fetchMediaErrorMessage: sprintf(
      /* translators: %s: media provider name. */
      __('Error loading media from %s', 'web-stories'),
      'ScorePlay'
    ),
  },
  [ProviderType.ASSETBANK]: {
    supportsCategories: false,
    supportsSearchByTags: false,
    requiresAuthorAttribution: false,
    fetchMediaErrorMessage: sprintf(
      /* translators: %s: media provider name. */
      __('Error loading media from %s', 'web-stories'),
      'Asset Bank'
    ),
  },
  [ProviderType.DROPBOX]: {
    supportsCategories: false,
    supportsSearchByTags: true,
    requiresAuthorAttribution: false,
    fetchMediaErrorMessage: sprintf(
      /* translators: %s: media provider name. */
      __('Error loading media from %s', 'web-stories'),
      'Dropbox'
    ),
    defaultPreviewWidth: 640,
    usesFolderAsQueryParam: true,
  },
  [ProviderType.PHOTOSHELTER]: {
    supportsCategories: false,
    supportsSearchByTags: true,
    requiresAuthorAttribution: false,
    fetchMediaErrorMessage: sprintf(
      /* translators: %s: media provider name. */
      __('Error loading media from %s', 'web-stories'),
      'Photoshelter'
    ),
    usesFolderAsQueryParam: true,
  },
  [ProviderType.STORYPILOT]: {
    supportsCategories: false,
    supportsSearchByTags: false,
    requiresAuthorAttribution: false,
    fetchMediaErrorMessage: sprintf(
      /* translators: %s: media provider name. */
      __('Error loading media from %s', 'web-stories'),
      'StoryPilot'
    ),
  },
  [ProviderType.OPUSCLIP]: {
    displayName: 'OpusClip',
    contentTypeFilter: ContentType.VIDEO,
    supportsCategories: false,
    supportsSearch: false,
    supportsSearchByTags: false,
    requiresFolderSelection: true,
    requiresAuthorAttribution: false,
    usesFolderAsQueryParam: true,
    fetchMediaErrorMessage: sprintf(
      /* translators: %s: media provider name. */
      __('Error loading media from %s', 'web-stories'),
      'OpusClip'
    ),
  },
};

export function is3pMediaProvider(providerCode) {
  return !!PROVIDERS[providerCode];
}
